import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import type { ICarrierInformation } from '@/interfaces';
import LogoTile from '../LogoTile';
import { CarrierSectionTitle, InsightsContainer, InsightsSection } from '../TrackingInsights.styles';
import { interpolate } from '../interpolate.utils';
import {
  CARRIER_LOGO_RADIUS,
  CarrierHeader,
  CarrierName,
  DetailGrid,
  DetailLabel,
  DetailLink,
  DetailText,
  Introduction,
  OverviewTitle,
} from './CarrierInformation.styles';

const LOGO_SIZE = 80;

export interface CarrierInformationProps {
  information: ICarrierInformation;
}

/**
 * Carrier identity block — the first thing on a carrier page after the hero.
 *
 * Both section titles are left-aligned here, unlike the centred titles the grid sections use;
 * that is how the design draws this block, and it reads as a page header rather than as one more
 * band down the page.
 *
 * The three details are deliberately not uniform: headquarters and phone are plain text, while the
 * website entry is a link labelled with the carrier's name. Every one is optional —
 * the long tail of smaller carriers publishes few of them, and a label standing over an empty
 * value reads as a broken page.
 */
const CarrierInformation: React.FC<CarrierInformationProps> = ({ information }) => {
  const { t } = useGlobalState();
  const { name, logoUrl, introduction, headquarters, phone, website } = information;
  const hasDetails = !!headquarters || !!phone || !!website;
  return (
    <InsightsSection>
      <InsightsContainer $wide>
        <CarrierSectionTitle type='h2'>{t(MasterTranslation.CarrierInformationTitle)}</CarrierSectionTitle>

        <CarrierHeader>
          <LogoTile name={name} logoUrl={logoUrl} size={LOGO_SIZE} radius={CARRIER_LOGO_RADIUS} />
          <CarrierName>{name}</CarrierName>
        </CarrierHeader>

        {introduction && <Introduction>{introduction}</Introduction>}

        {hasDetails && (
          <>
            <OverviewTitle type='h3'>{t(MasterTranslation.CarrierInformationOverview)}</OverviewTitle>

            {/* Filled row by row, so the grid reads headquarters | website over phone. */}
            <DetailGrid>
              {headquarters && (
                <div>
                  <DetailLabel>{t(MasterTranslation.CarrierInformationHeadquarters)}</DetailLabel>
                  <DetailText>{headquarters}</DetailText>
                </div>
              )}
              {website && (
                <div>
                  <DetailLabel>{t(MasterTranslation.CarrierInformationWebsite)}</DetailLabel>
                  <DetailText>
                    <DetailLink href={website} target='_blank' rel='noopener noreferrer nofollow'>
                      {interpolate(t(MasterTranslation.CarrierInformationWebsiteLink), 'carrier', name) ?? name}
                    </DetailLink>
                  </DetailText>
                </div>
              )}
              {phone && (
                <div>
                  <DetailLabel>{t(MasterTranslation.CarrierInformationPhone)}</DetailLabel>
                  <DetailText>{phone}</DetailText>
                </div>
              )}
            </DetailGrid>
          </>
        )}
      </InsightsContainer>
    </InsightsSection>
  );
};

export default CarrierInformation;
